import React, { useEffect, useMemo, useState } from 'react';
import { Checkbox } from '@material-ui/core';
import Modal from '../../../../../shared/components/core-ui/modal/modal.component';
import Button, {
  ButtonSize,
  ButtonType
} from '../../../../../shared/components/core-ui/button/button.component';
import {
  InputText,
  InputTextType
} from '../../../../../shared/components/core-ui/input-text/input-text.component';
import { Calendar } from '../../../../../shared/components/widgets/calendar/calendar.component';
import { BulkUpdateResultPanel } from '../../../../../shared/components/widgets/bulk-update-result/bulk-update-result.component';
import useWindowResizer from '../../../../../shared/hooks/window-resizer/window-resizer.hook';
import { birdToast } from '../../../../../shared/components/core-ui/toast';
import { isErrorInfo } from '../../../../../shared/interfaces/error-info.interface';
import { UnitTypeUiModel } from '../../../../../shared/ui-models/unit-type.ui-model';
import { BulkUpdate } from '../../../../../shared/models/unit-types.model';
import { bulkUpdateUnitTypeData } from '../../../../../UnitTypeModule/shared/services/data.service';
import {
  PriceAdjustmentDirection,
  type AdjustmentPeriod
} from '../../../../../UnitTypeModule/shared/services/custom-price-adjustment.service';
import {
  chunk,
  INELIGIBLE_LABELS,
  mergeBulkUpdateResults,
  planProjectPriceAdjustment,
  type UnitPricePlan
} from '../../../../shared/services/project-price-adjustment.service';

export interface AdjustProjectPricesModalProps {
  data: {
    projectName?: string;
    /** Every unit type assigned to this project. */
    unitTypes: UnitTypeUiModel[];
  };
  config: {
    showModal: boolean;
  };
  eventHandlers: {
    closeHandler: () => void;
    onApplied?: () => void;
  };
}

const MAX_PERCENTAGE = 500;
/**
 * The bulk endpoint now runs every unit to completion before answering, and each
 * unit costs a Birdnest save plus a Rentals United sync. Sending a whole project in
 * one request would sit on a single connection for minutes, so it goes in batches.
 */
const UNITS_PER_BATCH = 10;

/**
 * Turns the flat list of dates the calendar emits into [from, to] periods.
 * A trailing unpaired date is a half-finished selection and is ignored.
 */
const toPeriods = (dates: Date[]): AdjustmentPeriod[] => {
  const periods: AdjustmentPeriod[] = [];
  for (let index = 0; index + 1 < dates.length; index += 2) {
    periods.push({ from: dates[index], to: dates[index + 1] });
  }
  return periods;
};

/**
 * Raises or lowers the dynamic prices of every unit in a project by a percentage
 * over the selected periods. Each unit is adjusted against its own customPrices and
 * its own base price — this is not one price list copied across the project.
 */
export const AdjustProjectPricesModal: React.FC<AdjustProjectPricesModalProps> = ({
  data,
  config,
  eventHandlers
}) => {
  const isMobileView = useWindowResizer();
  const [direction, setDirection] = useState<PriceAdjustmentDirection>(
    PriceAdjustmentDirection.increase
  );
  const [percentage, setPercentage] = useState<string>('');
  const [selectedDates, setSelectedDates] = useState<Date[]>([]);
  const [excludedUnitIds, setExcludedUnitIds] = useState<Set<string>>(new Set());
  const [isConfirming, setIsConfirming] = useState<boolean>(false);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [progress, setProgress] = useState<{ done: number; total: number }>();
  const [result, setResult] = useState<BulkUpdate>();

  useEffect(() => {
    if (config.showModal) {
      setDirection(PriceAdjustmentDirection.increase);
      setPercentage('');
      setSelectedDates([]);
      setExcludedUnitIds(new Set());
      setIsConfirming(false);
      setProgress(undefined);
      setResult(undefined);
    }
  }, [config.showModal]);

  const periods = useMemo(() => toPeriods(selectedDates), [selectedDates]);
  const parsedPercentage = Number(percentage);
  const isPercentageValid =
    percentage !== '' &&
    Number.isFinite(parsedPercentage) &&
    parsedPercentage > 0 &&
    parsedPercentage <= MAX_PERCENTAGE &&
    (direction === PriceAdjustmentDirection.increase || parsedPercentage <= 100);
  const canPreview = isPercentageValid && periods.length > 0;

  const percentageError = (() => {
    if (percentage === '' || isPercentageValid) return undefined;
    if (!Number.isFinite(parsedPercentage) || parsedPercentage <= 0) {
      return 'Enter a percentage greater than 0.';
    }
    if (direction === PriceAdjustmentDirection.decrease && parsedPercentage > 100) {
      return "A decrease can't be more than 100%.";
    }
    return `Enter a percentage up to ${MAX_PERCENTAGE}%.`;
  })();

  /** One plan per unit, each computed against that unit's own prices. */
  const unitPlans = useMemo<UnitPricePlan[]>(
    () =>
      canPreview
        ? planProjectPriceAdjustment({
            unitTypes: data.unitTypes || [],
            periods,
            percentage: parsedPercentage,
            direction
          })
        : [],
    [canPreview, data.unitTypes, periods, parsedPercentage, direction]
  );

  const eligiblePlans = useMemo(
    () => unitPlans.filter((plan) => !plan.ineligible && !excludedUnitIds.has(plan.unit.id || '')),
    [unitPlans, excludedUnitIds]
  );
  const ineligiblePlans = useMemo(
    () => unitPlans.filter((plan) => plan.ineligible),
    [unitPlans]
  );

  const totals = useMemo(
    () =>
      eligiblePlans.reduce(
        (acc, plan) => ({
          adjusted: acc.adjusted + plan.adjusted,
          created: acc.created + plan.created
        }),
        { adjusted: 0, created: 0 }
      ),
    [eligiblePlans]
  );

  const toggleUnitExclusion = (unitId: string) => {
    setExcludedUnitIds((prev) => {
      const next = new Set(prev);
      if (next.has(unitId)) {
        next.delete(unitId);
      } else {
        next.add(unitId);
      }
      return next;
    });
    setIsConfirming(false);
  };

  const handleApply = () => {
    if (eligiblePlans.length === 0) return;

    setIsSubmitting(true);
    setProgress({ done: 0, total: eligiblePlans.length });

    const batches = chunk(eligiblePlans, UNITS_PER_BATCH);
    const sign = direction === PriceAdjustmentDirection.increase ? '+' : '-';
    const actionDescription = `${sign}${parsedPercentage}% over ${periods.length} period(s) · ${
      data.projectName || 'project'
    }`;

    // Batches run one after another and their tallies are merged, so a project of
    // any size reports a single honest set of numbers.
    const promise = (async () => {
      const batchResults: BulkUpdate[] = [];

      for (const batch of batches) {
        const response = await bulkUpdateUnitTypeData(
          batch.map((plan) => ({ id: plan.unit.id, customPrices: plan.customPrices })),
          actionDescription
        );

        if (isErrorInfo(response)) {
          throw response;
        }

        batchResults.push(response as BulkUpdate);
        setProgress((prev) => ({
          done: (prev?.done || 0) + batch.length,
          total: eligiblePlans.length
        }));
      }

      return mergeBulkUpdateResults(batchResults);
    })()
      .then((merged) => {
        eventHandlers.onApplied?.();
        setResult(merged);
        return merged;
      })
      .finally(() => {
        setIsSubmitting(false);
      });

    birdToast.promise(promise, {
      title: 'Adjust Project Prices',
      loadingMessage: `Adjusting ${eligiblePlans.length} unit(s)...`,
      successMessage: (merged: BulkUpdate) => {
        const parts = [`Birdnest ${merged.birdnest?.success ?? 0}/${eligiblePlans.length} updated`];
        if (merged.rentalsUnited) {
          parts.push(
            `Rentals United ${merged.rentalsUnited.success} synced, ${merged.rentalsUnited.failed} failed`
          );
        }
        return parts.join(' · ');
      },
      errorFailedMessage: 'Failed to adjust project prices. Please try again.',
      errorPermissionMessage: 'Permission denied'
    });
  };

  return (
    <Modal
      config={{ showModal: config.showModal, fullScreen: false, showClose: true, maxWidth: 'md' }}
      eventHandlers={{ closeModalHandler: eventHandlers.closeHandler }}
    >
      <div className="w-full p-4 md:p-8 font-gilroy-regular">
        <div className="mb-3 flex flex-col md:flex-row md:items-center md:justify-between gap-2">
          <div className="text-[1.9rem] leading-tight font-gilroy-bold text-gray-900">
            Adjust Dynamic Prices — Whole Project
          </div>
          {!result && (
            <div className="inline-flex items-center rounded-md border border-action-alert bg-action-alert bg-opacity-10 px-2 py-1 text-[1.2rem] text-neutral-800">
              <span className="mr-1.5 flex h-4 w-4 items-center justify-center rounded-full bg-action-alert text-white text-[1rem] font-gilroy-bold">
                !
              </span>
              Applies to every unit in {data.projectName || 'this project'}
            </div>
          )}
        </div>

        {result ? (
          <>
            <BulkUpdateResultPanel data={{ result }} />

            <div className="pt-4 border-t border-gray-200">
              <div className="flex flex-col justify-end md:flex-row gap-3">
                <Button
                  onClick={eventHandlers.closeHandler}
                  size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                >
                  Done
                </Button>
              </div>
            </div>
          </>
        ) : (
          <>
            <div className="rounded-lg border border-gray-200 bg-gradient-to-br from-gray-50 to-white px-4 py-3 mb-4 text-[1.3rem] leading-normal text-gray-600">
              Every unit is adjusted against <span className="font-gilroy-bold">its own</span>{' '}
              dynamic prices and its own base price — this does not copy one price list across the
              project. Ranges reaching outside a selected period are split so days outside keep
              their price, and days with no dynamic price get one from that unit&apos;s base price.
            </div>

            <div className="flex flex-col md:flex-row gap-4 mb-4">
              <div className="md:w-1/2">
                <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
                  Change
                </span>
                <div className="flex gap-2">
                  <Button
                    onClick={() => setDirection(PriceAdjustmentDirection.increase)}
                    type={
                      direction === PriceAdjustmentDirection.increase
                        ? ButtonType.primary
                        : ButtonType.primaryInverted
                    }
                    size={ButtonSize.medium}
                  >
                    Increase
                  </Button>
                  <Button
                    onClick={() => setDirection(PriceAdjustmentDirection.decrease)}
                    type={
                      direction === PriceAdjustmentDirection.decrease
                        ? ButtonType.primary
                        : ButtonType.primaryInverted
                    }
                    size={ButtonSize.medium}
                  >
                    Decrease
                  </Button>
                </div>
              </div>

              <div className="md:w-1/2">
                <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
                  Percentage
                </span>
                <InputText
                  data={{ value: percentage, placeholder: 'e.g. 20' }}
                  config={{
                    maxRows: 1,
                    autoFocus: true,
                    type: InputTextType.number,
                    error: !!percentageError
                  }}
                  eventHandlers={{
                    onChangeHandler: (value: string) => {
                      setPercentage(value);
                      setIsConfirming(false);
                    }
                  }}
                />
                {percentageError && (
                  <div className="mt-1.5 text-[1.2rem] text-action-alert">{percentageError}</div>
                )}
              </div>
            </div>

            <div className="mb-4">
              <span className="block text-[1.2rem] font-gilroy-medium uppercase tracking-[0.07em] text-gray-500 mb-2">
                Periods
              </span>
              <Calendar
                data={{
                  calenderView: true,
                  showDateInput: false,
                  multipleRanges: true
                }}
                eventHandlers={{
                  onChange: (dates) => {
                    setSelectedDates(Array.isArray(dates) ? (dates as Date[]) : []);
                    setIsConfirming(false);
                  }
                }}
              />
              <div className="mt-1.5 text-[1.2rem] text-gray-500">
                {periods.length === 0
                  ? 'Select a start and an end date to add a period. Click a selected period again to remove it.'
                  : `${periods.length} period${periods.length === 1 ? '' : 's'} selected.`}
              </div>
            </div>

            {canPreview && (
              <div className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
                <div className="flex flex-col md:flex-row md:justify-between md:items-center gap-3 px-4 py-3 border-b border-gray-200 bg-white">
                  <div>
                    <div className="text-[1.5rem] font-gilroy-bold text-gray-900">
                      {eligiblePlans.length} of {unitPlans.length} unit
                      {unitPlans.length === 1 ? '' : 's'} will change
                    </div>
                    <div className="text-[1.2rem] text-gray-500">
                      {totals.adjusted} existing price{totals.adjusted === 1 ? '' : 's'} adjusted,{' '}
                      {totals.created} new price{totals.created === 1 ? '' : 's'} created
                    </div>
                  </div>
                  <div className="text-[1.3rem] font-gilroy-medium text-gray-600">
                    {direction === PriceAdjustmentDirection.increase ? '+' : '-'}
                    {parsedPercentage}% over {periods.length} period
                    {periods.length === 1 ? '' : 's'}
                  </div>
                </div>

                <div className="max-h-[260px] overflow-y-auto">
                  {unitPlans.map((plan) => {
                    const unitId = plan.unit.id || '';
                    const isExcluded = excludedUnitIds.has(unitId);

                    return (
                      <div
                        key={unitId}
                        className="flex items-center justify-between gap-3 px-4 py-2 border-b border-gray-200 last:border-b-0"
                      >
                        <div className="flex items-center min-w-0">
                          <Checkbox
                            color="primary"
                            checked={!plan.ineligible && !isExcluded}
                            disabled={!!plan.ineligible}
                            onChange={() => toggleUnitExclusion(unitId)}
                          />
                          <div className="ml-1 min-w-0">
                            <div
                              className={
                                'text-[1.4rem] truncate ' +
                                (plan.ineligible
                                  ? 'text-gray-400 font-gilroy-regular'
                                  : 'text-gray-900 font-gilroy-medium')
                              }
                            >
                              {plan.unit.name || unitId}
                            </div>
                            {plan.ineligible && (
                              <div className="text-[1.2rem] text-gray-500">
                                {INELIGIBLE_LABELS[plan.ineligible]}
                              </div>
                            )}
                          </div>
                        </div>
                        {!plan.ineligible && (
                          <div className="text-[1.2rem] text-gray-500 whitespace-nowrap shrink-0">
                            {plan.adjusted} adjusted · {plan.created} new
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {ineligiblePlans.length > 0 && (
                  <div className="px-4 py-2 border-t border-gray-200 bg-gray-50 text-[1.2rem] leading-normal text-gray-600">
                    {ineligiblePlans.length} unit{ineligiblePlans.length === 1 ? '' : 's'} left
                    untouched. Monthly units have no dynamic prices, and a unit with no base price
                    in USD has nothing to derive a new price from.
                  </div>
                )}
              </div>
            )}

            <div className="pt-4 border-t border-gray-200">
              {isSubmitting && progress && (
                <div className="mb-3 text-[1.4rem] text-gray-900">
                  Adjusting {progress.done} of {progress.total} unit(s)...
                </div>
              )}
              {isConfirming && !isSubmitting && (
                <div className="mb-3 text-[1.4rem] leading-normal text-gray-900">
                  This will{' '}
                  {direction === PriceAdjustmentDirection.increase ? 'increase' : 'decrease'} prices
                  by {parsedPercentage}% across {eligiblePlans.length} unit
                  {eligiblePlans.length === 1 ? '' : 's'} in{' '}
                  {data.projectName || 'this project'}. Apply?
                </div>
              )}
              <div className="flex flex-col justify-end md:flex-row gap-3">
                <Button
                  onClick={() =>
                    isConfirming ? setIsConfirming(false) : eventHandlers.closeHandler()
                  }
                  type={ButtonType.primaryInverted}
                  size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  disabled={isSubmitting}
                >
                  {isConfirming ? 'Back' : 'Cancel'}
                </Button>
                <Button
                  onClick={() => (isConfirming ? handleApply() : setIsConfirming(true))}
                  size={isMobileView ? ButtonSize.full : ButtonSize.medium}
                  disabled={eligiblePlans.length === 0 || isSubmitting}
                >
                  {isSubmitting
                    ? 'Applying...'
                    : isConfirming
                    ? `Yes, adjust ${eligiblePlans.length} unit${
                        eligiblePlans.length === 1 ? '' : 's'
                      }`
                    : 'Apply to Project'}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </Modal>
  );
};

export default AdjustProjectPricesModal;
